import { Tag, Tooltip } from 'antd';

/** What one artifact cost to generate, as returned on its DTO. */
export interface AiCost {
  costUsd: number;
  /** Provider calls whose model had no known rate; NOT included in costUsd. */
  unpriced: number;
  calls: number;
}

const money = (usd: number): string =>
  // Sub-cent spend is normal — a single narration line costs a fraction of a
  // cent — so "$0.00" would be a rounding artefact rather than a fact.
  usd > 0 && usd < 0.01 ? '<$0.01' : `$${usd.toFixed(2)}`;

/**
 * Shows what an artifact cost to generate.
 *
 * Renders nothing at all when no spend has been recorded — a generation that
 * has not run yet has no cost, and a confident "$0.00" would be a lie about
 * something that simply has not happened.
 *
 * When some of the run used a model with no known rate, the figure is marked as
 * a floor rather than a total. A partial number presented as a total is the one
 * outcome worth designing against here: it is the version someone would budget
 * against and be wrong.
 */
export const CostTag = ({ cost }: { cost: AiCost | null | undefined }) => {
  if (!cost || cost.calls === 0) return null;

  const detail = `${String(cost.calls)} AI call${cost.calls === 1 ? '' : 's'}`;

  if (cost.unpriced > 0) {
    return (
      <Tooltip
        title={`${detail}. ${String(cost.unpriced)} used a model with no configured price, so the real cost is higher than this. Set AI_PRICING_JSON to include them.`}
      >
        <Tag color="orange">≥ {money(cost.costUsd)}</Tag>
      </Tooltip>
    );
  }

  return (
    <Tooltip title={detail}>
      <Tag color="default">{money(cost.costUsd)}</Tag>
    </Tooltip>
  );
};
